/*
 * Copyright (c) 2026. Spin in het Web B.V. Apeldoorn NL.
 * Deze software is géén open source. Het auteursrecht van deze software
 * ligt bij Spin in het Web B.V. en/of Jellybean B.V. en/of de auteur.
 * Gebruik van deze software is onderhevig aan een licentieovereenkomst.
 * www.spininhetweb.nl
 */

/**
 * sihwdatepicker.ts
 * 30-08-2026 - Jelmer Jellema - Spin in het Web B.V. Apeldoorn NL
 * Gegenereerd met Claude (AI), 30-08-2026
 * Component Sihwdatepicker
 * Selector: sihwdatepicker
 *
 * Standalone, op Luxon gebaseerde port van SihwDatepicker uit sihw-ng.
 * Eenvoudige input met kalenderpopup op basis van bsDatepicker uit ngx-bootstrap.
 * <sihwdatepicker [(ngModel)]="record.datum" dateType="mysql|datetime"></sihwdatepicker>
 *
 * De waarde is standaard een sql-datumstring (jjjj-mm-dd); met dateType="datetime"
 * is het een Luxon DateTime. Een lege waarde is null en is geldig.
 *
 * Overige attributen: minDate / maxDate (sql-string, DateTime of de speciale waarde
 * 'now' voor vandaag), placeholder, placement en bsConfig (zie de documentatie van
 * bsDatepicker op https://valor-software.com/ngx-bootstrap/#/datepicker).
 *
 * Let op: importeer in de scss van de applicatie de styling van de kalender:
 * @import 'node_modules/ngx-bootstrap/datepicker/bs-datepicker';
 */

import {Component, ElementRef, HostBinding, inject, Input, ViewChild} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormsModule,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator
} from '@angular/forms';
import {BsDatepickerModule, BsLocaleService} from 'ngx-bootstrap/datepicker';
import {defineLocale} from 'ngx-bootstrap/chronos';
import {nlLocale} from 'ngx-bootstrap/locale';
import {DateTime} from 'luxon';
import {Sihwlog} from '../sihwlog';
import {hash} from '../types';

/**
 * De waarden die als datum geaccepteerd worden: een sql-datumstring, een Luxon DateTime,
 * of niets. Bij minDate en maxDate is 'now' een geldige string voor vandaag.
 */
export type Sihwdatepickerdatum = string | DateTime | null | undefined;

//eenmalige registratie van de Nederlandse locale van de kalender
let nlGeregistreerd = false;

/**
 * Registreert de Nederlandse locale bij ngx-bootstrap, de eerste keer dat er een
 * datepicker gemaakt wordt. Staat buiten de class zodat het niet per instantie gebeurt.
 */
function registreerNlLocale() {
    if (!nlGeregistreerd) {
        defineLocale('nl', nlLocale);
        nlGeregistreerd = true;
    }
}

@Component({
    standalone: true,
    selector: 'sihwdatepicker',
    templateUrl: './sihwdatepicker.html',
    styleUrls: ['sihwdatepicker.scss'],
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: Sihwdatepicker, multi: true},
        {provide: NG_VALIDATORS, useExisting: Sihwdatepicker, multi: true} //doorgeven valid state
    ],
    imports: [
        FormsModule,
        BsDatepickerModule
    ]
})
export class Sihwdatepicker implements ControlValueAccessor, Validator {
    static idTeller = 1;
    ctrlid = `sihwdatepicker_${Sihwdatepicker.idTeller++}`;

    @Input('bsConfig') bsConfig_in: object = {}; //config om door te geven aan de datepicker
    @Input('dateType') dateType: ('mysql' | 'datetime') = 'mysql'; //waarde als sql-string of als DateTime

    //copy props
    @Input() placement?: 'top' | 'bottom' | 'left' | 'right';
    @Input() placeholder: string = '';

    date: Date | string | null = null; //de interne value (string bij ongeldig)

    @ViewChild('invoerveld') private invoerveld?: ElementRef<HTMLInputElement>; //het invoerelement zelf, zie herstelInvoer()
    private ruweTekst: string | null = null; //wat de gebruiker zelf getypt heeft, zie getypt()

    @Input('disabled') disabled: boolean = false; //de disabled value, en readonly op de host. Dat gebruiken we als alternatief voor disabled, wat we niet goed kunnen zetten

    @HostBinding('attr.readonly') get hostReadonly() {
        return this.disabled ? true : null;
    } //zorg voor verwijderen readonly als niet nodig

    //minDate en maxDate zijn setters op interne vars, zodat we validiteit kunnen herchecken
    private _minDate: string | DateTime | null = null;
    private _maxDate: string | DateTime | null = null;

    private readonly localeService = inject(BsLocaleService);
    private log: Sihwlog = new Sihwlog();
    private hostControl?: AbstractControl; //wordt gezet via de validator (dirty hack die het leven makkelijker maakt)
    private meldGewijzigd: (_: any) => void = (_) => {
    }; //controlvalueaccessor
    onTouched: () => void = () => {
    }; //controlvalueaccessor

    constructor() {
        registreerNlLocale();
        this.localeService.use('nl'); //locale voor de datepicker. Kan optie worden
    }

    /**
     * De configuratie die aan bsDatepicker wordt doorgegeven: onze standaarden,
     * overschrijfbaar met wat er via het bsConfig-attribuut binnenkomt.
     *
     * minDate en maxDate gaan bewust via deze config en niet via de gelijknamige inputs van
     * bsDatepicker. De kalender schakelt in beide gevallen de dagen buiten de grenzen uit,
     * maar de inputdirective van ngx-bootstrap verschuift bij die inputs een getypte datum
     * stilletjes naar de grens (en laat het veld daarbij ook nog op ongeldig staan). Zo houdt
     * de gebruiker wat hij typt, en melden alleen wij de fout mindate of maxdate.
     * Een grens die via het bsConfig-attribuut binnenkomt blijft staan zolang wij er zelf geen
     * hebben; onze eigen grens gaat voor.
     */
    get bsConfig(): object {
        const config: hash<any> = Object.assign(
            {
                isAnimated: true,
                adaptivePosition: true,
                containerClass: 'theme-default'
                //overschrijfbare zaken
            },
            this.bsConfig_in
        );
        const ondergrens = this.toDate(this.minDate);
        const bovengrens = this.toDate(this.maxDate);
        if (ondergrens) {
            config['minDate'] = ondergrens;
        }
        if (bovengrens) {
            config['maxDate'] = bovengrens;
        }
        return config;
    }

    ////////////////////////////////////// input getters en setters

    get minDate(): string | DateTime | null {
        return this._minDate;
    }

    /**
     * De vroegst toegestane datum, als sql-string of DateTime. De waarde 'now' staat voor vandaag.
     */
    @Input('minDate') set minDate(d: Sihwdatepickerdatum) {
        this._minDate = (d === 'now') ? DateTime.now().toISODate() : (d || null);
        this.revalidate(); //validate als dat kan
    }

    get maxDate(): string | DateTime | null {
        return this._maxDate;
    }

    /**
     * De laatst toegestane datum, als sql-string of DateTime. De waarde 'now' staat voor vandaag.
     */
    @Input('maxDate') set maxDate(d: Sihwdatepickerdatum) {
        this._maxDate = (d === 'now') ? DateTime.now().toISODate() : (d || null);
        this.revalidate(); //validate als dat kan
    }

    //////////////////////////////// MODEL /////////////////////////////
    /**
     * De gebruiker typt (of plakt) in het veld: onthoud de letterlijke tekst, zodat
     * herstelInvoer() hem kan terugzetten als de datum onleesbaar blijkt.
     */
    getypt() {
        this.ruweTekst = this.invoerveld?.nativeElement.value ?? null;
    }

    /**
     * De gebruiker heeft een datum gekozen of getypt: meld de nieuwe waarde naar boven,
     * in het formaat dat bij dateType hoort. Een onleesbare waarde melden we letterlijk,
     * zodat de validator hem als ongeldig kan aanmerken.
     */
    gewijzigd(_nieuwewaarde: any) {
        if (!this.date) {
            this.date = null;
            this.ruweTekst = null;
            this.meldGewijzigd(null);
            return;
        }
        const nieuw = this.datumDateTime(this.date);
        if (!nieuw.isValid) {
            this.herstelInvoer();
            this.meldGewijzigd(this.date);
            return;
        }
        this.ruweTekst = null; //gelezen datum: de weergave is weer aan de datepicker
        this.log.debug(`Value nu`, nieuw.toISODate());
        this.meldGewijzigd(this.dateType === 'mysql' ? nieuw.toISODate() : nieuw);
    }

    ///////////////////////////////////////// CONTROLVALUEACCESSOR //////////////////
    /**
     * Nieuwe waarde wordt aangeboden
     * @param obj
     */
    writeValue(obj: any): void {
        //de waarde komt als DateTime of als sql-string binnen, dat regelen we
        this.date = this.toDate(obj);
        this.ruweTekst = null; //een waarde van buiten gaat voor op wat er getypt was
        this.log.debug(`sihwdatepicker set value`, obj, this.date);
    }

    registerOnChange(fn: any): void {
        this.meldGewijzigd = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.log.debug(`sihwdatepicker setDisabled`, isDisabled);
        this.disabled = isDisabled;
    }

    //////////////////////////////// VALIDATOR /////////////////////////
    /**
     * Controleert de ingevulde datum: leeg mag, onleesbaar niet, en de datum moet binnen
     * minDate en maxDate vallen (beide inclusief).
     */
    validate(control: AbstractControl): ValidationErrors | null {
        //ha, onze host control
        this.hostControl ??= control;

        //gaat het goed met date?
        this.log.debug(`sihwdatepicker. Valid?`, this.date);
        if (!this.date) {
            this.log.debug('- leeg dus okee');
            return null; //prima - leeg maar prima
        }

        const datum = this.isoDatum(this.date);
        if (!datum) {
            this.log.debug('- invalid date');
            return {'invalid_date': true};
        }

        const ondergrens = this.minDate ? this.isoDatum(this.minDate) : null;
        if (ondergrens && datum < ondergrens) {
            this.log.warn(`Datepicker: voor de mindate`, datum, ondergrens);
            return {'mindate': true};
        }

        const bovengrens = this.maxDate ? this.isoDatum(this.maxDate) : null;
        if (bovengrens && datum > bovengrens) {
            this.log.warn(`Datepicker: na de maxdate`, datum, bovengrens);
            return {'maxdate': true};
        }

        this.log.debug('- ja, okee');
        return null;
    }

    /////// eigen helper
    /**
     * Laat de host-control opnieuw valideren, bijvoorbeeld na een gewijzigde minDate of maxDate.
     */
    revalidate() {
        //als we al een keer gevalideerd hebben, hebben we de control
        if (this.hostControl) {
            this.hostControl.updateValueAndValidity();
        }
        //anders komt er nog wel een begin-validering
    }

    /**
     * Zet de getypte tekst terug in het invoerveld. Dat is nodig omdat de inputdirective van
     * ngx-bootstrap een onleesbare invoer buiten de Angular-binding om vervangt door de tekst
     * "Invalid date" (formatDate() van een Invalid Date, via renderer.setProperty). We wachten
     * met een microtask tot die schrijfactie geweest is en zetten alleen terug zolang de waarde
     * inderdaad onleesbaar is. Zo houdt de gebruiker zijn eigen tekst om te corrigeren, terwijl
     * validate() de fout invalid_date blijft melden.
     */
    private herstelInvoer() {
        queueMicrotask(() => {
            const veld = this.invoerveld?.nativeElement;
            if (!veld || this.ruweTekst === null) {
                return; //niets om terug te zetten
            }
            if (this.date && !this.datumDateTime(this.date).isValid && veld.value !== this.ruweTekst) {
                veld.value = this.ruweTekst;
            }
        });
    }

    //////////////////////////// helper ///////////////////////////////
    /**
     * Zet een waarde (sql-string of DateTime) om in een js date, zoals bsDatepicker die
     * verwacht. Levert null bij een lege of onleesbare waarde.
     * @param val
     */
    toDate(val: Sihwdatepickerdatum): Date | null {
        if (!val) {
            //null etc
            return null;
        }
        const datum = this.datumDateTime(val);
        return datum.isValid ? datum.toJSDate() : null;
    }

    /**
     * Zet een waarde om in een DateTime op middernacht in de eigen tijdzone. Er is wat gedoe
     * met utc en het vergelijken ervan; door de datum via de kale datumdelen opnieuw op te
     * bouwen zitten we altijd in de huidige tz. Een onleesbare waarde levert een DateTime
     * op waarvan isValid false is.
     * @param waarde sql-string, DateTime of de js date die uit de kalender komt
     */
    datumDateTime(waarde: string | Date | DateTime | null): DateTime {
        const datum = this.parseDatum(waarde);
        return datum.isValid ? DateTime.fromISO(<string>datum.toISODate()) : datum;
    }

    /**
     * Levert de datum van een waarde als sql-datumstring, of null als de waarde niet te lezen is.
     * Handig om datums te vergelijken zonder gedoe met tijd en tijdzone.
     * @param waarde
     */
    private isoDatum(waarde: string | Date | DateTime | null): string | null {
        return this.datumDateTime(waarde).toISODate();
    }

    /**
     * Leest een waarde uit als DateTime, zonder normalisatie. Een string proberen we eerst
     * als sql-datum en anders als iso-datum; een lege of onleesbare waarde levert een
     * ongeldige DateTime op.
     * @param waarde
     */
    private parseDatum(waarde: string | Date | DateTime | null): DateTime {
        if (DateTime.isDateTime(waarde)) {
            return waarde;
        }
        if (waarde instanceof Date) {
            return DateTime.fromJSDate(waarde);
        }
        if (typeof waarde === 'string') {
            const sqldatum = DateTime.fromSQL(waarde);
            return sqldatum.isValid ? sqldatum : DateTime.fromISO(waarde);
        }
        return DateTime.invalid('geen datum');
    }
}
